import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { formatRp } from "@/lib/tiers";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Copy,
  CheckCircle2,
  ExternalLink,
  Clock,
  Sparkles,
  AlertCircle,
  Loader2,
  ShieldCheck,
} from "lucide-react";

export default function SaweriaAutoCheckout({ tier, amount, onFallbackManual }) {
  const nav = useNavigate();
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [copiedAmount, setCopiedAmount] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [timeLeft, setTimeLeft] = useState(7200); // 2 hours
  const [isApproved, setIsApproved] = useState(false);

  const pollIntervalRef = useRef(null);

  const basePrices = {
    elite_cultist: 10000,
    hardcore_cultist: 25000,
    god_tier_supporter: 500000,
  };
  const effectiveAmount = amount || basePrices[tier] || 25000;

  // 1. User clicks button on page: GENERATES CODE & OPENS POPUP (DOES NOT OPEN SAWERIA YET!)
  const handleOpenPopup = async () => {
    try {
      setLoading(true);
      const { data } = await api.post("/payments/saweria/create", { tier });
      setOrder(data);

      if (data.expires_at) {
        const diff = Math.max(0, Math.floor((new Date(data.expires_at).getTime() - Date.now()) / 1000));
        setTimeLeft(diff);
      }

      // Open the modal popup!
      setIsModalOpen(true);
    } catch (err) {
      toast.error("Gagal menyiapkan tagihan Saweria: " + formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  // 2. User clicks inside the popup: OPENS SAWERIA IN NEW TAB
  const handleOpenSaweriaTab = () => {
    if (!order) return;
    const saweriaUrl = order.saweria_url || "https://saweria.co/kyoukareimei";
    window.open(saweriaUrl, "_blank", "noopener,noreferrer");
    toast.success("Tab Saweria berhasil dibuka! Silakan masukkan nominal persis.");
  };

  // Countdown timer (only active once invoice is generated)
  useEffect(() => {
    if (!order || isApproved) return;
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [order, isApproved]);

  // Polling check status every 3 seconds
  useEffect(() => {
    if (!order || isApproved) return;

    pollIntervalRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/payments/saweria/check/${order.order_id}`);
        if (data.status === "approved") {
          setIsApproved(true);
          clearInterval(pollIntervalRef.current);
          toast.success("🎉 Pembayaran Diterima! Membership kamu sudah aktif!");
          setTimeout(() => {
            nav("/dashboard");
          }, 2500);
        }
      } catch (err) {
        // Silently retry
      }
    }, 3000);

    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
    };
  }, [order, isApproved, nav]);

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(String(text));
    if (type === "amount") {
      setCopiedAmount(true);
      setTimeout(() => setCopiedAmount(false), 2000);
      toast.info("Nominal unik berhasil disalin!");
    } else {
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
      toast.info("Kode invoice disalin!");
    }
  };

  const formatCountdown = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const shortCode = order ? `KYK-${order.order_id.replace(/-/g, "").slice(0, 6).toUpperCase()}` : "";

  return (
    <>
      {/* =========================================================================
          KARTU DI HALAMAN (STEP 2)
          ========================================================================= */}
      <div className="p-6 rounded-2xl bg-gradient-to-b from-zinc-900 to-black border border-white/10 space-y-5 text-center">
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-400 font-semibold mb-1">
            Pembayaran Otomatis Saweria
          </div>
          <div className="text-3xl font-black font-display text-white">
            {order ? formatRp(order.exact_amount) : formatRp(effectiveAmount)}
          </div>
          {order && (
            <div className="text-xs text-rose-400 font-medium mt-1">
              Tagihan aktif (+{order.unique_code} kode unik)
            </div>
          )}
        </div>

        <p className="text-zinc-300 text-xs max-w-md mx-auto leading-relaxed">
          {order
            ? "Tagihan unik kamu sudah siap. Klik tombol di bawah untuk melihat rincian pembayaran atau membuka Saweria."
            : "Klik tombol di bawah untuk melihat rincian kode unik pembayaran sebelum membuka Saweria."}
        </p>

        <Button
          type="button"
          onClick={order ? () => setIsModalOpen(true) : handleOpenPopup}
          disabled={loading}
          className="w-full py-6 text-base font-bold bg-gradient-to-r from-rose-600 via-rose-500 to-rose-600 hover:from-rose-500 hover:to-rose-400 text-white shadow-xl shadow-rose-950 transition transform hover:-translate-y-0.5"
          data-testid="open-saweria-btn"
        >
          {loading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Menyiapkan Kode Unik...</span>
            </div>
          ) : order ? (
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-300" />
              <span>Lihat Kode Pembayaran ({formatRp(order.exact_amount)})</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              <span>Dapatkan Kode Unik & Bayar</span>
            </div>
          )}
        </Button>

        {order && (
          <div className="pt-2 flex items-center justify-between text-xs text-zinc-400 border-t border-white/5">
            <div className="flex items-center gap-1.5">
              <Loader2 className="w-3.5 h-3.5 text-rose-400 animate-spin" />
              <span>Mendengarkan transaksi otomatis...</span>
            </div>
            <span className="font-mono text-zinc-500">{formatCountdown(timeLeft)}</span>
          </div>
        )}

        <div className="pt-1 flex items-center justify-center gap-2 text-[11px] text-zinc-400">
          <ShieldCheck className="w-4 h-4 text-emerald-400" />
          <span>Mendukung QRIS, GoPay, OVO, DANA, ShopeePay</span>
        </div>
      </div>

      {/* =========================================================================
          POPUP MODAL KODE UNIK (MUNCUL SEBELUM BUKA SAWERIA)
          ========================================================================= */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-md bg-zinc-950 border border-rose-500/40 text-white p-6 shadow-2xl rounded-2xl">
          <DialogHeader className="space-y-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-pulse" />
                <span className="text-[11px] font-bold uppercase tracking-wider text-rose-400">
                  Instruksi Pembayaran Saweria
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-xs text-zinc-400 bg-white/5 px-2.5 py-1 rounded-full border border-white/10">
                <Clock className="w-3.5 h-3.5 text-amber-400" />
                <span className="font-mono font-bold text-white">{formatCountdown(timeLeft)}</span>
              </div>
            </div>
            <DialogTitle className="text-xl font-black font-display text-white pt-2">
              Transfer Persis Sesuai Kode Unik
            </DialogTitle>
          </DialogHeader>

          {isApproved ? (
            <div className="py-8 text-center space-y-3 animate-in fade-in zoom-in-95">
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center mx-auto text-emerald-400">
                <CheckCircle2 className="w-10 h-10" />
              </div>
              <h3 className="text-2xl font-black font-display text-white">PEMBAYARAN DITERIMA!</h3>
              <p className="text-zinc-300 text-xs">Membership kamu sudah aktif otomatis. Mengalihkan ke dashboard...</p>
            </div>
          ) : order ? (
            <div className="space-y-5 pt-2">
              {/* Box Nominal Unik Besar */}
              <div className="text-center p-5 rounded-xl bg-rose-950/30 border border-rose-500/50 space-y-2">
                <div className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold">
                  Nominal yang Harus Ditransfer
                </div>
                <div className="flex items-center justify-center gap-3">
                  <span className="text-3xl font-black font-display text-rose-200 tracking-tight">
                    {formatRp(order.exact_amount)}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(order.exact_amount, "amount")}
                    className="border-rose-500/40 hover:bg-rose-500/20 text-rose-200 h-8 px-2.5 gap-1.5"
                  >
                    {copiedAmount ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                    <span className="text-xs font-bold">{copiedAmount ? "Tersalin!" : "Salin"}</span>
                  </Button>
                </div>
                <div className="text-[11px] text-amber-300 flex items-center justify-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0 text-amber-400" />
                  <span>Termasuk 3 digit kode unik (<strong>+{order.unique_code}</strong>)</span>
                </div>
              </div>

              {/* Langkah-langkah Pembayaran */}
              <div className="space-y-2.5 text-xs text-zinc-300 bg-white/[0.03] p-4 rounded-xl border border-white/5">
                <div className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-rose-600/30 text-rose-300 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">1</span>
                  <span>Salin nominal <strong>{formatRp(order.exact_amount)}</strong> di atas.</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-rose-600/30 text-rose-300 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">2</span>
                  <span>Klik tombol <strong>Buka Saweria Sekarang</strong> di bawah untuk membuka halaman pembayaran.</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-rose-600/30 text-rose-300 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">3</span>
                  <span>Di Saweria, masukkan nominal <strong>{order.exact_amount}</strong> dan selesaikan pembayaran via QRIS / E-Wallet.</span>
                </div>
              </div>

              {/* Tombol Utama di dalam Popup: Buka Saweria */}
              <Button
                type="button"
                onClick={handleOpenSaweriaTab}
                className="w-full py-6 text-base font-bold bg-gradient-to-r from-rose-600 via-rose-500 to-rose-600 hover:from-rose-500 hover:to-rose-400 text-white shadow-lg shadow-rose-950 transition transform hover:-translate-y-0.5"
              >
                <ExternalLink className="w-5 h-5 mr-2" />
                Buka Saweria Sekarang
              </Button>

              {/* Status Listening di dalam Popup */}
              <div className="pt-3 border-t border-white/5 flex items-center justify-between text-[11px] text-zinc-400">
                <div className="flex items-center gap-1.5">
                  <Loader2 className="w-3.5 h-3.5 text-rose-400 animate-spin" />
                  <span>Menunggu sinyal pembayaran dari Saweria...</span>
                </div>
                <span className="text-zinc-500">Auto-Detect</span>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
